import { useParams } from 'react-router-dom';
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api, { resetAuthErrorState } from '../services/api';
import { useAuth } from '../contexts/AuthContext'; // Import your Auth context
import dayjs from 'dayjs'; // Import dayjs
import { useLoader } from './LoaderContext';

const LeadContext = createContext();

export const useLead = () => useContext(LeadContext);

export const LeadProvider = ({ children }) => {
  const { id } = useParams();
  // Get authentication state from AuthContext
  const { isAuthenticated } = useAuth();
  const { showLoader, hideLoader } = useLoader();
  // State for pipeline data (sidebar filters and saved searches)
  const [pipelineData, setPipelineData] = useState({
    statusFilters: [],
    savedSearches: []
  });

  // State for currently applied filters
  const [appliedFilters, setAppliedFilters] = useState({});
  
  // State for selected status and search in the sidebar
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSearchId, setSelectedSearchId] = useState(null);
  
  
  // State for all leads and filtered leads
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [allLeadsUnfiltered, setAllLeadsUnfiltered] = useState([]); // Original unfiltered data for sidebar counts
  const [statusCounts, setStatusCounts] = useState({}); // Status counts from API
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    current_page: 1,
    page_size: 50,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });

  const [filterOptions, setFilterOptions] = useState({});
  const [defaultFilters, setDefaultFilters] = useState({});
  
  // Track if we've done initial data loading
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Add a ref to track the last API call to prevent duplicate calls
  const lastCallRef = useRef(null);
  
  // Add a ref to track the last status counts API call
  const lastStatusCountsCallRef = useRef(null);
  
  // In-flight promise ref for status counts deduplication
  const statusCountsPromiseRef = useRef(null);
  
  // Add a ref for debouncing API calls
  const debounceTimeoutRef = useRef(null);
  
  // Add a ref to track the current request ID
  const currentRequestIdRef = useRef(null);

  // Add a state for tracking assignment errors
  const [assignmentErrors, setAssignmentErrors] = useState([]);

  // Add new state variables for lead details
  const [currentLead, setCurrentLead] = useState(null);
  const [leadActivities, setLeadActivities] = useState([]);
  const [leadNotes, setLeadNotes] = useState([]);
  const [leadCalls, setLeadCalls] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [leadStageOrders, setLeadStageOrders] = useState({});
  const [leadNoteTemplates, setLeadNoteTemplates] = useState({});

  // Add a state to track API errors
  const [hasApiError, setHasApiError] = useState(false);

  // Add export state
  const [exportData, setExportData] = useState(null);
  const [lastExportFilters, setLastExportFilters] = useState(null);

  // Wrapper function to clear export data when filters change
  const setAppliedFiltersWithExportClear = useCallback((newFilters) => {
    // Clear export data when filters change
    setExportData(null);
    setLastExportFilters(null);
    setAppliedFilters(newFilters);
  }, []);

  // Common method to build API payload for both fetchLeads and exportLeads
  const buildApiPayload = useCallback((filters = {}, dateRangeState = null, isExport = false) => {
    const requestPayload = {
      // Always include pagination and sorting
      page: filters.page || 1,
      page_size: filters.page_size || 50,
      sort_field: filters.sort_field || '-updated_at',
      sort_order: filters.sort_order || 'desc',
      is_deleted: filters.is_deleted || false,
      all_results: filters.all_results || false,
      is_export: isExport
    };

    // Add other filters (excluding pagination and sorting fields)
    Object.keys(filters).forEach(key => {
      if (!['page', 'page_size', 'sort_field', 'sort_order', "is_deleted", 'search', 'all_results', 'is_export', 'budget'].includes(key)) {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          // Convert filter values to arrays for backend compatibility
          if (Array.isArray(filters[key])) {
            requestPayload[key] = filters[key];
          } else {
            requestPayload[key] = [filters[key]];
          }
        }
      }
    });

    // Handle budget filter separately - it should remain as an object, not be converted to an array
    if (filters.budget && filters.budget !== null && filters.budget !== undefined && filters.budget !== '') {
      requestPayload.budget = filters.budget;
    }

    // Add date range if provided
    if (filters.date_range) {
      requestPayload.date_range = filters.date_range;
    }

    return requestPayload;
  }, []);

  
  // Fetch pipeline data (status filters and saved searches)
  const fetchPipelineData = async (dateRangeState = null) => {
    if (!isAuthenticated || hasApiError) return null;
    
    try {
      const response = await api.get('/lms/leads/filters/pipeline/');
      
      // Ensure we have expected structures even if API returns null
      const statusFilters = response.data.status_filters || [];
      const savedSearches = response.data.saved_searches || [];
      
      // Fetch status counts (including saved search counts) from the API
      const statusCountsData = await fetchStatusCounts(dateRangeState);
      
      // Update saved searches with counts from the API
      if (statusCountsData.saved_searches && Array.isArray(statusCountsData.saved_searches)) {
        savedSearches.forEach(search => {
          const apiSearch = statusCountsData.saved_searches.find(apiSearch => apiSearch.id === search.id);
          if (apiSearch && apiSearch.count !== undefined) {
            search.count = apiSearch.count;
          }
        });
      }      
      setPipelineData({
        statusFilters: statusFilters,
        savedSearches: savedSearches
      });
      
      return response.data;
    } catch (error) {      
      // Log detailed error information
      if (error.response) {
        console.error('Error response status:', error.response.status);
      }
      
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      // Set default empty arrays to prevent undefined errors
      setPipelineData({
        statusFilters: [],
        savedSearches: []
      });
      
      return null;
    }
  };
  
  // Fetch status counts for sidebar (promise-based dedup: concurrent identical calls share one request)
  // pipelineFilters: active non-source filters (lead_stage, unassigned, status, etc.)
  // isDeleted: whether the deleted-leads view is active
  const fetchStatusCounts = useCallback(async (dateRangeState = null, pipelineFilters = null, isDeleted = false) => {
    if (!isAuthenticated || hasApiError) return {};
    
    // Default to "all results" when no date range or filter is All
    const allResults = dateRangeState == null
      ? true
      : (dateRangeState.all_results === true || dateRangeState.filter?.id === 'All');
    
    const pipelineKey = pipelineFilters ? JSON.stringify(pipelineFilters) : '';
    const deletedKey = isDeleted ? '_del' : '';
    const currentKey = allResults
      ? `all_results${deletedKey}_${pipelineKey}`
      : `${dateRangeState.currentStart?.format('YYYY-MM-DD')}_${dateRangeState.currentEnd?.format('YYYY-MM-DD')}${deletedKey}_${pipelineKey}`;
    
    // If there's an in-flight or recently resolved request with the same key, reuse it
    if (currentKey === lastStatusCountsCallRef.current && statusCountsPromiseRef.current) {
      return statusCountsPromiseRef.current;
    }
    
    lastStatusCountsCallRef.current = currentKey;
    
    const requestPayload = allResults
      ? { all_results: true }
      : {
          date_range: {
            start_date: dateRangeState.currentStart.format('YYYY-MM-DD'),
            end_date: dateRangeState.currentEnd.format('YYYY-MM-DD')
          },
          all_results: false
        };

    if (pipelineFilters && Object.keys(pipelineFilters).length > 0) {
      const { search, ...restFilters } = pipelineFilters;
      if (Object.keys(restFilters).length > 0) {
        requestPayload.pipeline_filters = restFilters;
      }
      if (search && search.trim()) {
        requestPayload.search = search.trim();
      }
    }
    if (isDeleted) {
      requestPayload.is_deleted = true;
    }
    
    const promise = api.post('/lms/leads/status-counts/', requestPayload)
      .then(response => {
        // Clear refs after resolve so the next explicit call (e.g. after delete) always fetches fresh data
        statusCountsPromiseRef.current = null;
        lastStatusCountsCallRef.current = null;
        if (response && response.data) {
          return response.data;
        }
        console.error('Invalid status counts API response structure:', response);
        return {};
      })
      .catch(error => {
        statusCountsPromiseRef.current = null;
        lastStatusCountsCallRef.current = null;
        
        if (error.response) {
          console.error('Error response data:', error.response.data);
        } else if (error.request) {
          console.error('Error request:', error.request);
        } else {
          console.error('Error message:', error.message);
        }
        
        if (error.isAuthError) {
          setHasApiError(true);
        }
        
        return {};
      });
    
    statusCountsPromiseRef.current = promise;
    return promise;
  }, [isAuthenticated, hasApiError]);

  // Fetch leads with applied filters
  const fetchLeads = useCallback(async (filters = {}, dateRangeState = null, forceRefresh = false, isexport=false) => {
    // Prevent duplicate calls with better comparison
    const callKey = safeStringify({ 
      filters: filters || {}, 
      dateRangeState: dateRangeState ? {
        start_date: dateRangeState.currentStart?.format('YYYY-MM-DD'),
        end_date: dateRangeState.currentEnd?.format('YYYY-MM-DD'),
        filter_id: dateRangeState.filter?.id
      } : null, 
      isexport: isexport || false
    });
    
    // Check if this is the same call as the last one (unless forceRefresh is true)
    if (lastCallRef.current === callKey && !forceRefresh) {
      console.log('Preventing duplicate API call for:', callKey);
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (isLoading) {
      console.log('API call already in progress, skipping...');
      return;
    }
    
    // Update the last call reference BEFORE making the call
    lastCallRef.current = callKey;
    
    // Generate a unique request ID for this call
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    console.log('Making API call for:', callKey, 'Request ID:', requestId);

    // Show loader for all fetch operations
    showLoader();
    setIsLoading(true);
    setError(null);

    try {
      // Build the request payload using common method
      const requestPayload = buildApiPayload(filters, dateRangeState, isexport);
      console.log('API Request Payload:', requestPayload)

      const response = await api.post('/lms/leads/', requestPayload);
      
      // Check if this is still the current request (prevent stale responses)
      if (currentRequestIdRef.current !== requestId) {
        console.log('Ignoring stale response for request ID:', requestId);
        return;
      }
      
      const responseData = response.data;

      const leadsData = responseData.leads;
      const paginationInfo = responseData.pagination;
      console.log("paginationInfo ", paginationInfo)
      setPaginationInfo(paginationInfo);
      setLeads(leadsData);
      setFilteredLeads(leadsData);

    } catch (error) {
      // Check if this is still the current request (prevent stale error handling)
      if (currentRequestIdRef.current !== requestId) {
        console.log('Ignoring stale error for request ID:', requestId);
        return;
      }
      
      console.error('Error fetching leads:', error);
      setError('Failed to fetch leads. Please try again.');
      setLeads([]);
      setFilteredLeads([]);
      setPaginationInfo({
        total_count: 0,
        current_page: 1,
        page_size: 50,
        total_pages: 1,
        has_next: false,
        has_previous: false
      });
      // Reset the last call reference on error to allow retry
      lastCallRef.current = null;
    } finally {
      // Hide loader and reset loading state
      hideLoader();
      setIsLoading(false);
    }
  }, [isAuthenticated, hasApiError, showLoader, hideLoader, buildApiPayload]); // Add dependencies to prevent unnecessary re-renders

  // Debounced version of fetchLeads to prevent rapid successive calls
  const debouncedFetchLeads = useCallback((filters = {}, dateRangeState = null, delay = 300, isexport=false) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set a new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchLeads(filters, dateRangeState, false, isexport);
    }, delay);
  }, [fetchLeads]);
  
  // Create a new saved search
  const createSavedSearch = async (newBookmark) => {
    if (hasApiError) throw new Error('API error occurred');
    
    try {
      const response = await api.post('/lms/bookmarks/', {
        bookmarks: newBookmark
      });
      
      // Update the pipeline data with the new saved search
      setPipelineData(prev => ({
        ...prev,
        savedSearches: [...prev.savedSearches, response.data]
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error creating saved search:', error);
      
      // Check if it's an auth error
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      throw error;
    }
  };
  
  // Update an existing saved search
  const updateSavedSearch = async (searchId, updatedData) => {
    if (hasApiError) throw new Error('API error occurred');
    
    try {
      const response = await api.put(`/lms/bookmarks/${searchId}/`, {'bookmark': updatedData});
      
      // Update the pipeline data with the updated saved search
      setPipelineData(prev => ({
        ...prev,
        savedSearches: prev.savedSearches.map(search => 
          search.id === searchId ? response.data : search
        )
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error updating saved search:', error);
      
      // Check if it's an auth error
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      throw error;
    }
  };
  
  // Delete a saved search
  const deleteSavedSearch = async (searchId) => {
    if (hasApiError) throw new Error('API error occurred');
    
    try {
      await api.delete(`/lms/bookmarks/${searchId}/`);
      
      // Remove the deleted saved search from pipeline data
      setPipelineData(prev => ({
        ...prev,
        savedSearches: prev.savedSearches.filter(search => search.id !== searchId)
      }));
      
      // If the deleted search was selected, reset selection
      if (selectedSearchId === `search_${searchId}`) {
        setSelectedSearchId(null);
        setSelectedStatus('all');
        setFilteredLeads(leads); // Reset to show all leads
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting saved search:', error);
      
      // Check if it's an auth error
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      throw error;
    }
  };
  
  // Apply status filter using API - Enhanced to support complex filter configurations
  const applyStatusFilter = (statusId, filterCriteria = null, dateRangeState = null) => {
    if (hasApiError) return;    
    // Check if this is the same filter being applied again
    if (selectedStatus === statusId && safeStringify(appliedFilters) === safeStringify(filterCriteria)) {
      return;
    }
    
    setSelectedSearchId(null);
    setSelectedStatus(statusId);
    
    let newFilters = filterCriteria ? { ...filterCriteria } : (statusId === 'all' ? {} : { status: statusId });
    
    if (newFilters.not_contacted) {
      delete newFilters.not_contacted;
      newFilters.last_contacted_at__isnull = true;
    }
    
    console.log("applyStatusFilter - statusId:", statusId, "newFilters:", newFilters);
    setAppliedFilters(newFilters);
    
    // Pass dateRangeState explicitly
    fetchLeads(newFilters, dateRangeState);
  };
  
  // Apply saved search filter using API
  const applySavedSearch = (search, dateRangeState = null) => {
    if (hasApiError) return;
    
    try {      
      // Set selected search
      setSelectedSearchId(search.id);
      setSelectedStatus(`search_${search.id}`);
      
      // Make a defensive copy of the search criteria
      const searchCriteria = search && search.search_criteria ? { ...search.search_criteria } : {};      
      
      // Convert agents to agent_id for UI consistency
      if (searchCriteria.agents && !searchCriteria.agent_id) {
        searchCriteria.agent_id = searchCriteria.agents;
        delete searchCriteria.agents;
      }
      
      // Process budget_min/budget_max to budget object if needed
      if ((searchCriteria.budget_min !== undefined || searchCriteria.budget_max !== undefined) && 
          !searchCriteria.budget) {
        searchCriteria.budget = {
          min_value: searchCriteria.budget_min || 0,
          max_value: searchCriteria.budget_max || 100000000
        };
        delete searchCriteria.budget_min;
        delete searchCriteria.budget_max;
      }
      
      // Process last_contacted fields to last_contacted object if needed
      if ((searchCriteria.last_contacted_from !== undefined || searchCriteria.last_contacted_to !== undefined) && 
          !searchCriteria.last_contacted) {
        searchCriteria.last_contacted = {
          start_time: searchCriteria.last_contacted_from,
          end_time: searchCriteria.last_contacted_to
        };
        delete searchCriteria.last_contacted_from;
        delete searchCriteria.last_contacted_to;
      }
      
      // Ensure all array fields are actually arrays
      const arrayFields = ['status', 'lead_stage', 'tags', 'agent_id', 'source', 'bhk_type', 'lead_urgency', 'lead_lcp', 'occupation'];
      arrayFields.forEach(field => {
        if (searchCriteria[field] !== undefined && !Array.isArray(searchCriteria[field])) {
          searchCriteria[field] = [searchCriteria[field]];
        }
      });
      
      // Apply search criteria as filters
      setAppliedFilters(searchCriteria);
      
      // Pass dateRangeState explicitly
      fetchLeads(searchCriteria, dateRangeState);
      
      return true;
    } catch (error) {
      console.error('Error applying saved search:', error);
      // Display error in console for debugging
      if (error.response) {
        console.error('Error response data:', error.response.data);
      }
      return false;
    }
  };
  
  // Add new lead
  const addNewLead = async (leadData) => {
    if (hasApiError) throw new Error('API error occurred');
    
    setIsLoading(true);
    try {
      // Handle both single lead and bulk lead creation
      const response = await api.post('/lms/leads/create/', {'new_lead': leadData});
      // Refresh the leads data after successful creation
      // await fetchLeads();
      // Return the full response data
      return response.data;
    } catch (error) {
      console.error('Error creating lead:', error);
      
      // Check if it's an auth error
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update the saveTags function to handle the new payload format
  const saveTags = async (tagsData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    setIsLoading(true);
    try {
      // API now expects data in format: { lead_ids: [1, 2, 3], tags: ["tag1", "tag2"] }
      const response = await api.post('/lms/leads/add-new-tags/', tagsData);
      
      // Process the response - assuming it returns a similar passed/errored format
      if (response.data) {
        const { passed, errored } = response.data;
        
        // Process successfully updated leads
        if (passed && Array.isArray(passed) && passed.length > 0) {
          // Create a map of successfully updated leads
          const successfulUpdates = new Map(
            passed.map(lead => [lead.id, lead])
          );
          
          // Update both leads and filtered leads with the new tags
          setLeads(prevLeads => {
            return prevLeads.map(lead => {
              const updatedLead = successfulUpdates.get(lead.id);
              if (updatedLead) {
                return {
                  ...lead,
                  tags: updatedLead.tags || tagsData.tags // Use response tags or request tags
                };
              }
              return lead;
            });
          });
          
          setFilteredLeads(prevLeads => {
            return prevLeads.map(lead => {
              const updatedLead = successfulUpdates.get(lead.id);
              if (updatedLead) {
                return {
                  ...lead,
                  tags: updatedLead.tags || tagsData.tags // Use response tags or request tags
                };
              }
              return lead;
            });
          });
        }
        
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error updating tags:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize data on authentication changes
  useEffect(() => {
    const initializeData = async () => {
      if (!hasInitiallyLoaded && isAuthenticated && id === null) {
        try {
          setIsLoading(true);
          await Promise.all([
            // fetchPipelineData(),
            fetchLeads({}), // Pass empty filters for initial load
          ]);
          setHasInitiallyLoaded(true);
        } catch (error) {
          console.error('Error initializing lead data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    initializeData();
  }, [isAuthenticated, id]);

  // Cleanup effect to clear timeouts and refs on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      lastCallRef.current = null;
      lastStatusCountsCallRef.current = null;
      statusCountsPromiseRef.current = null;
      currentRequestIdRef.current = null;
    };
  }, []);
  
  // Update the assignAgents function in LeadContext.js
  const assignAgents = async (assignmentData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    setIsLoading(true);
    try {
      // API: { lead_ids, agent_id, notes, optional new_stage, new_status } — assign-agents applies stage/status when provided
      const response = await api.post('/lms/leads/assign-agents/', { assignees: assignmentData });
      
      // Process the response data with the usual format:
      // {"passed": [{id: 9, agent_id: 32, agent_name: "Surabhi Jain"}], "errored": []}
      if (response.data) {
        const { passed, errored } = response.data;
        
        // Store errored lead IDs in state for UI highlighting
        if (errored && Array.isArray(errored)) {
          setAssignmentErrors(errored.map(item => item.id));
        } else {
          setAssignmentErrors([]);
        }
        
        // Process successfully updated leads
        if (passed && Array.isArray(passed) && passed.length > 0) {
          // Create a map of successfully updated leads
          const successfulUpdates = new Map(
            passed.map(lead => [lead.id, lead])
          );
          
          // Update both leads and filtered leads with new agent assignments
          setLeads(prevLeads => {
            return prevLeads.map(lead => {
              const updatedLead = successfulUpdates.get(lead.id);
              if (updatedLead) {
                return {
                  ...lead,
                  agent_id: updatedLead.agent_id,
                  agent_name: updatedLead.agent_name,
                  ...(updatedLead.stage != null && { stage: updatedLead.stage }),
                  ...(updatedLead.status != null && { status: updatedLead.status }),
                };
              }
              return lead;
            });
          });
          
          setFilteredLeads(prevLeads => {
            return prevLeads.map(lead => {
              const updatedLead = successfulUpdates.get(lead.id);
              if (updatedLead) {
                return {
                  ...lead,
                  agent_id: updatedLead.agent_id,
                  agent_name: updatedLead.agent_name,
                  ...(updatedLead.stage != null && { stage: updatedLead.stage }),
                  ...(updatedLead.status != null && { status: updatedLead.status }),
                };
              }
              return lead;
            });
          });
        }
        
        // Return the full response for the caller to process if needed
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error assigning agents:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add the function to add notes to leads
  const addLeadNotes = async (notesData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    setIsLoading(true);
    try {
      // API expects data in format: { lead_ids: [1, 2, 3], notes: "text" }
      const response = await api.post('/lms/leads/add-notes/', notesData);
      return response;
    } catch (error) {
      console.error('Error adding notes:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add this function to update lead status
  const updateLeadStatus = async (statusData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    setIsLoading(true);
    try {
      // API expects data in format: { lead_ids: [1, 2, 3], status: "new", notes: "text" }
      const response = await api.post('/lms/leads/update-status/', statusData);
      
      // Process the response
      if (response.data) {
        const { passed, errored } = response.data;
        
        // Update leads state with new status
        if (passed && Array.isArray(passed) && passed.length > 0) {
          // Create a map of successfully updated leads
          const successfulUpdates = new Map(
            passed.map(lead => [lead.id, lead])
          );
          
          // Update both leads and filtered leads with the new status
          setLeads(prevLeads => {
            return prevLeads.map(lead => {
              const updatedLead = successfulUpdates.get(lead.id);
              if (updatedLead) {
                return {
                  ...lead,
                  status: statusData.status,
                  status_updated_at: new Date().toISOString(),
                  // Add any other properties from the response
                };
              }
              return lead;
            });
          });
          
          setFilteredLeads(prevLeads => {
            return prevLeads.map(lead => {
              const updatedLead = successfulUpdates.get(lead.id);
              if (updatedLead) {
                return {
                  ...lead,
                  status: statusData.status,
                  status_updated_at: new Date().toISOString(),
                  // Add any other properties from the response
                };
              }
              return lead;
            });
          });
        }
        
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error updating lead status:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function to move leads to a new stage
  const deleteLead = async (payload) => {
    setIsLoading(true);
    try {
      const response = await api.post('/lms/leads/delete/', payload);
      return response;
    } catch (error) {
      console.error('Error deleting lead:', error.response);
      // Return the error response instead of throwing
      if (error.response) {
        return error.response;
      } else {
        // If no response object, create a mock response
        return {
          status: 500,
          data: {
            passed: 0,
            errored: payload.lead_ids || [],
            message: error.message || 'Network error'
          }
        };
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function to restore deleted leads
  const restoreLead = async (payload) => {
    setIsLoading(true);
    try {
      const response = await api.post('/lms/leads/restore/', payload);
      return response;
    } catch (error) {
      console.error('Error restoring lead:', error.response);
      // Return the error response instead of throwing
      if (error.response) {
        return error.response;
      } else {
        // If no response object, create a mock response
        return {
          status: 500,
          data: {
            passed: 0,
            errored: payload.lead_ids || [],
            message: error.message || 'Network error'
          }
        };
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reset the API error state
  const resetApiErrors = () => {
    setHasApiError(false);
    resetAuthErrorState(); // Reset the global auth error state in api.js
  };

  // Force refresh by clearing the last call reference
  const forceRefresh = () => {
    lastCallRef.current = null;
    lastStatusCountsCallRef.current = null;
    statusCountsPromiseRef.current = null;
  };
  
  // Fetch a single lead detail
  const fetchLeadDetail = async (leadId) => {
    if (!isAuthenticated) return null;
    
    setIsLoading(true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/`);
      setCurrentLead(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead details:', error);
      
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch lead activities
  const fetchLeadActivities = async (leadId) => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await api.get(`/lms/leads/${leadId}/activities/`);
      setLeadActivities(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead activities:', error);
      return null;
    }
  };

  // Fetch lead notes
  const fetchLeadNotes = async (leadId) => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await api.get(`/lms/leads/${leadId}/notes/`);
      setLeadNotes(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead notes:', error);
      return null;
    }
  };

  // Fetch lead calls
  const fetchLeadCalls = async (leadId) => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await api.get(`/lms/leads/${leadId}/calls/`);
      setLeadCalls(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead calls:', error);
      return null;
    }
  };

  // Fetch site visits
  const fetchSiteVisits = async (leadId) => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await api.get(`/lms/leads/${leadId}/site-visits/`);
      setSiteVisits(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching site visits:', error);
      return null;
    }
  };

  // Schedule a site visit
  const scheduleSiteVisit = async (leadId, visitData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try {
      const response = await api.post(`/lms/leads/${leadId}/schedule-visit/`, visitData);
      
      // Refresh site visits and lead detail after scheduling
      await Promise.all([
        fetchSiteVisits(leadId),
        fetchLeadDetail(leadId)
      ]);
      
      return response.data;
    } catch (error) {
      console.error('Error scheduling site visit:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new task for a lead
  const createLeadTask = async (leadId, taskData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try {
      const response = await api.post(`/lms/leads/${leadId}/tasks/`, taskData);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Send a WhatsApp message
  const sendWhatsAppMessage = async (leadId, message) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const response = await api.post(`/lms/leads/${leadId}/send-whatsapp/`, { message });
      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      throw error;
    }
  };

  // Load all lead data at once
  const loadLeadAllData = async (leadId) => {
    if (!isAuthenticated || !leadId) return;
    
    setIsLoading(true);
    try {
      await Promise.all([
        fetchLeadDetail(leadId),
        fetchLeadActivities(leadId),
        fetchLeadNotes(leadId),
        fetchLeadCalls(leadId),
        fetchSiteVisits(leadId)
      ]);
    } catch (error) {
      console.error('Error loading lead data:', error);
    } finally {
      setIsLoading(false);
    }
  };





  // Add new lead (legacy - kept for backward compatibility)
  const importLeadFromCSV = async (leadData, options = {}) => {
    if (hasApiError) throw new Error('API error occurred');
    const { dryRun = false, extraPayload = {} } = options;
    const payload = {
      leads: leadData,
      ...extraPayload
    };
    if (dryRun) {
      payload.dry_run_mode = true;
    }
    
    setIsLoading(true);
    try {
      // Use longer timeout for CSV imports
      // Calculate timeout based on row count: 1.5 seconds per row, minimum 60s, maximum 1200s (20 min for 10K rows)
      const rowCount = leadData?.length || 0;
      const calculatedTimeout = Math.max(60000, Math.min(1200000, rowCount * 150));
      
      const response = await api.post('/lms/leads/import/', payload, {
        timeout: calculatedTimeout,
        // Add headers to indicate this is a long-running request
        headers: {
          'X-Request-Type': 'csv-import',
          'X-Row-Count': rowCount.toString()
        }
      });
      return response;
    } catch (error) {
      if (error.isAuthError) {
        setHasApiError(true);
      }
      
      // Provide better error messages for timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const timeoutError = new Error(
          `Request timed out after processing ${leadData?.length || 0} rows. ` +
          `The server may still be processing your request. Please wait a few minutes and check if the leads were imported. ` +
          `If the issue persists, try splitting your CSV into smaller files (max 2000 rows per file).`
        );
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 2-Phase Job-Based Import Functions
  const validateLeadImport = async (leadData, extraPayload = {}) => {
    const payload = { leads: leadData, ...extraPayload };
    const rowCount = leadData?.length || 0;
    const timeout = Math.max(10000, Math.min(60000, rowCount * 10));
    const response = await api.post('/lms/leads/import/validate/', payload, { timeout });
    return response.data;
  };

  
  const commitLeadImport = async () => {
    const response = await api.post('/lms/leads/import/commit/', {}, { timeout: 5000 });
    return response.data;
  };

  
  const getLeadImportStatus = async () => {
    const response = await api.get('/lms/leads/import/status/', { timeout: 5000 });
    return response.data;
  };

  const discardLeadImport = async () => {
    const response = await api.delete('/lms/leads/import/discard/', { timeout: 5000 });
    return response.data;
  };

  // Helper function to convert leads data to CSV format
  const convertLeadsToCSV = (leads) => {
    if (!Array.isArray(leads) || leads.length === 0) {
      return '';
    }

    // Define CSV headers based on the lead structure
    const headers = [
      'Lead ID',
      'Name',
      'Email',
      'Mobile',
      'Source',
      'Status',
      'Stage',
      'Tags',
      'Agent',
      'Budget Min',
      'Budget Max',
      'Created At',
      'Updated At',
      'Last Contacted',
      'Site Visit Date',
      'Site Visit Time',
      'Site Visit Status'
    ];

    // Convert leads to CSV rows
    const csvRows = leads.map(lead => {
      const contact = lead.contact || {};
      const agent = lead.agent || {};
      const budget = lead.budget || {};
      const siteVisit = lead.site_visit || {};
      const tags = Array.isArray(lead.tags) ? lead.tags.join(', ') : '';

      return [
        lead.lead_id || '',
        contact.name || '',
        contact.email || '',
        contact.mobile || '',
        lead.source_verbose || lead.source || '',
        lead.status_verbose || lead.status || '',
        lead.stage_verbose || lead.stage || '',
        tags,
        agent.name || '',
        budget.min || '',
        budget.max || '',
        lead.created_at || '',
        lead.updated_at || '',
        lead.last_contacted || '',
        siteVisit.date || '',
        siteVisit.time || '',
        siteVisit.status_verbose || siteVisit.status || ''
      ];
    });

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map(row => 
        row.map(field => {
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const escaped = String(field).replace(/"/g, '""');
          if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
            return `"${escaped}"`;
          }
          return escaped;
        }).join(',')
      )
      .join('\n');

    return csvContent;
  };

  // Utility function to safely serialize objects without circular references
  const safeStringify = (obj) => {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      // Handle circular references
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      
      // Filter out non-serializable properties
      if (value instanceof Element || value instanceof Event || value instanceof Function) {
        return '[Non-serializable]';
      }
      
      // Handle React components and other complex objects
      if (value && typeof value === 'object' && value.constructor && value.constructor.name !== 'Object' && value.constructor.name !== 'Array') {
        // Try to extract only serializable properties
        const serializable = {};
        for (const [k, v] of Object.entries(value)) {
          if (typeof v !== 'function' && typeof v !== 'object' || v === null || Array.isArray(v)) {
            serializable[k] = v;
          }
        }
        return Object.keys(serializable).length > 0 ? serializable : '[Complex Object]';
      }
      
      return value;
    });
  };

  // Function to clean objects before serialization
  const cleanForSerialization = (obj) => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => cleanForSerialization(item));
    }
    
    if (typeof obj === 'object') {
      // Check if it's a DOM element, React component, or other non-serializable object
      if (obj instanceof Element || obj instanceof Event || obj instanceof Function) {
        return '[Non-serializable]';
      }
      
      // Check for React Fiber nodes and other React internals
      if (obj.constructor && (
        obj.constructor.name === 'FiberNode' || 
        obj.constructor.name === 'Fiber' ||
        obj.constructor.name === 'ReactElement' ||
        obj.constructor.name === 'ReactComponent'
      )) {
        return '[React Component]';
      }
      
      // Clean regular objects
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        // Skip React internal properties
        if (key.startsWith('__react') || key.startsWith('_react') || key === 'stateNode') {
          continue;
        }
        
        cleaned[key] = cleanForSerialization(value);
      }
      return cleaned;
    }
    
    return obj;
  };

  // Separate export function that doesn't interfere with existing functionality
  const exportLeadsData = useCallback(async (filters = {}, dateRangeState = null) => {
    if (hasApiError) {
      console.warn('API error occurred, skipping export');
      return null;
    }
    
    // Show loader at the start of export process
    showLoader();
    
    try {
      // Build export payload using common method
      const exportPayload = buildApiPayload(filters, dateRangeState, true);
      console.log('Export Request Payload:', exportPayload);

      const response = await api.post('/lms/leads/', exportPayload);
      
      const responseData = response.data;
      
      // Handle export data
      if (responseData && responseData.leads && Array.isArray(responseData.leads)) {
        setExportData(responseData.leads);
        setLastExportFilters(safeStringify(cleanForSerialization({ filters, dateRangeState })));
        
        // Convert leads data to CSV format
        const csvData = convertLeadsToCSV(responseData.leads);
        
        // Download CSV file
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        return responseData.leads;
      }
      
      // If no export data, just log a warning instead of throwing error
      console.warn('No export data received from server');
      return null;
    } catch (error) {
      console.error('Error exporting leads:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      // Don't throw error, just return null to prevent page break
      return null;
    } finally {
      // Hide loader when export process completes (success or error)
      hideLoader();
    }
  }, [hasApiError, buildApiPayload, showLoader, hideLoader]);


  const importLeadsUpdateFromCSV = async (leadData, options = {}) => {
    if (hasApiError) throw new Error('API error occurred');
    const { dryRun = false, extraPayload = {} } = options;
    const payload = {
      leads: leadData,
      ...extraPayload
    };
    if (dryRun) {
      payload.dry_run_mode = true;
    }
    const response = await api.post(`/lms/leads/update-csv/`, payload);
    return response.data;
  };


  const validateLeadSourceCSV = async (leadData) => {
    const response = await api.post(`/lms/leads/csv-update-source/validate/`, { leads: leadData }, { timeout: 60000 });
    return response.data;
  };

  const commitLeadSourceCSV = async (leadData) => {
    const response = await api.post(`/lms/leads/csv-update-source/commit/`, { leads: leadData }, { timeout: 60000 });
    return response.data;
  };

  // Expose functions in context value
  const contextValue = {
    leads,
    filteredLeads,
    allLeadsUnfiltered,
    setFilteredLeads,
    isLoading,
    error,
    appliedFilters,
    setAppliedFilters: setAppliedFiltersWithExportClear, // Use wrapper function
    fetchLeads,
    fetchStatusCounts,
    statusCounts,
    setStatusCounts,
    paginationInfo,
    setPaginationInfo,
    createSavedSearch,
    updateSavedSearch,
    deleteSavedSearch,
    applySavedSearch,
    applyStatusFilter,
    fetchPipelineData,
    pipelineData,
    addNewLead,
    saveTags,
    assignAgents,
    addLeadNotes,
    updateLeadStatus,
    deleteLead,
    restoreLead,
    fetchLeadDetail,
    fetchLeadActivities,
    fetchLeadNotes,
    fetchLeadCalls,
    fetchSiteVisits,
    scheduleSiteVisit,
    createLeadTask,
    sendWhatsAppMessage,
    loadLeadAllData,
    // other functions
    resetApiErrors,
    selectedStatus,
    setSelectedStatus,
    selectedSearchId,
    setSelectedSearchId,
    // Add missing stage and status flow data
    leadStageOrders,
    importLeadFromCSV,
    validateLeadImport,
    commitLeadImport,
    getLeadImportStatus,
    discardLeadImport,
    importLeadsUpdateFromCSV,
    leadNoteTemplates,
    assignmentErrors,
    forceRefresh,
    debouncedFetchLeads,
    // Export-related functions
    exportData,
    lastExportFilters,
    setExportData,
    setLastExportFilters,
    exportLeadsData,
    buildApiPayload,

    // Lead CSV Update Source
    validateLeadSourceCSV,
    commitLeadSourceCSV,

  };
  
  return (
    <LeadContext.Provider value={contextValue}>
      {children}
    </LeadContext.Provider>
  );
};